import { useAuth } from "@assetwatch/auth";
import { useQuery } from "@tanstack/react-query";
import { post } from "aws-amplify/api";
import { useEffect } from "react";
import i18n from "../i18n/config";

const LANGUAGE_PREFERENCE = 21;

type SupportedLanguage = {
  lid: number;
  code: string;
  name: string;
};

type PreferenceItem = {
  prefid: number;
  prefval: string;
};

function asPreferenceItem(value: unknown): PreferenceItem | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const prefid = record.prefid;
  const prefval = record.prefval;

  if (typeof prefid !== "number" || typeof prefval !== "string") {
    return null;
  }

  return { prefid, prefval };
}

async function getSupportedLanguages(): Promise<SupportedLanguage[]> {
  const { body } = await post({
    apiName: "apiVeroUser",
    path: "/list",
    options: {
      body: {
        meth: "getSupportedLanguages",
      },
    },
  }).response;

  const json = await body.json();
  return Array.isArray(json) ? (json as SupportedLanguage[]) : [];
}

export function useLanguageSync(enabled = true) {
  const { user, isAuthenticated } = useAuth();
  const { data: supportedLanguages = [] } = useQuery({
    queryKey: ["enterprise-supported-languages"],
    queryFn: getSupportedLanguages,
    enabled: enabled && isAuthenticated,
    staleTime: Infinity,
  });

  const rawPreferences = (user as { preferences?: unknown[] } | null)
    ?.preferences;
  const preferences = Array.isArray(rawPreferences)
    ? rawPreferences
        .map(asPreferenceItem)
        .filter((pref): pref is PreferenceItem => pref !== null)
    : [];
  const langPreferenceValue = preferences.find(
    (pref) => pref.prefid === LANGUAGE_PREFERENCE,
  )?.prefval;
  const normalizedLangPreferenceValue = langPreferenceValue
    ? String(langPreferenceValue).trim()
    : undefined;
  const resolvedLanguageCode = supportedLanguages.find(
    (lang) =>
      lang.code === normalizedLangPreferenceValue ||
      String(lang.lid) === normalizedLangPreferenceValue ||
      lang.name === normalizedLangPreferenceValue,
  )?.code;

  useEffect(() => {
    if (!enabled || !isAuthenticated || !resolvedLanguageCode) return;

    if (resolvedLanguageCode !== i18n.language) {
      i18n.changeLanguage(resolvedLanguageCode);
    }
  }, [enabled, isAuthenticated, resolvedLanguageCode]);
}
